export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  PARK_ADMIN = 'park_admin',
  COMPANY_ADMIN = 'company_admin',
  USER = 'user'
}

export type TwoFaEnforcement = 'disabled' | 'optional' | 'required';
export type TwoFaLevelEnforcement = 'inherit' | 'optional' | 'required';
export type TwoFaMode = 'every_login' | 'trusted_device';

export interface ExternalGuest {
  name: string;
  email?: string;
  company?: string;
}

export interface Park {
  id: string;
  name: string;
  address: string;
  description: string;
  logoUrl: string | null;
  isActive: boolean;
  twofaEnforcement?: TwoFaLevelEnforcement;
  receptionEmail?: string | null;
  receptionGuestFields?: string[];
  calendarFeedEnabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type AuthSource = 'local' | 'ldap' | 'oidc' | 'saml';
export type SsoProtocol = 'oidc' | 'saml';

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  companyId: string;
  parkId?: string | null;
  twofaEnabled?: boolean;
  addonRoles?: string[];
  isActive?: boolean;
  authSource?: AuthSource;
  inviteToken?: boolean | string | null;
  hasSeenTour?: boolean;
  deskBookingEnabled?: boolean;
  createdAt?: string;
}

export interface Company {
  id: string;
  name: string;
  address: string;
  parkId: string;
  twofaEnforcement?: TwoFaLevelEnforcement;
  deskBookingEnabled?: boolean;
  setupPending?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface MeetingRoom {
  id: string;
  name: string;
  capacity: number;
  amenities: string[];
  floor: string;
  address: string;
  description: string;
  isActive: boolean;
  parkId: string;
  openingHour?: number | null;
  closingHour?: number | null;
  lockedToCompanyIds?: string[]; // Companies that can book this room (empty = open to all)
  lockedToCompanies?: Company[];
  quickBookDurations?: number[]; // Available quick booking durations in minutes for device
  bookingEmail?: string | null; // Optional email address for booking-by-email (iMIP)
  // Per-room IMAP credentials (password never returned — use hasImapPassword instead)
  imapHost?: string | null;
  imapPort?: number | null;
  imapUser?: string | null;
  imapMailbox?: string | null;
  hasImapPassword?: boolean; // true if an IMAP password is stored on the server
  // Per-room SMTP for sending iMIP replies (reuses IMAP user/pass for auth)
  smtpHost?: string | null;    // Defaults to imapHost if null
  smtpPort?: number | null;    // 587 = STARTTLS (default), 465 = SSL
  smtpSecure?: boolean | null; // false = STARTTLS, true = SSL
  calendarFeedEnabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Settings {
  id: string;
  openingHour: number;
  closingHour: number;
  timezone: string;
  timeFormat: '12h' | '24h';
  twofaEnforcement?: TwoFaEnforcement;
  twofaMode?: TwoFaMode;
  twofaTrustedDeviceDays?: number;
  updatedAt?: string;
  // System banner
  bannerEnabled?: boolean;
  bannerMessage?: string | null;
  bannerLevel?: 'info' | 'warning' | 'critical';
  bannerStartsAt?: string | null;
  bannerEndsAt?: string | null;
}

export interface Booking {
  id: string;
  roomId: string;
  userId: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  attendees: string[];
  externalGuests?: ExternalGuest[];
  status: 'confirmed' | 'cancelled';
  room?: MeetingRoom;
  user?: User;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthResponse {
  token: string;
  user?: User;
  requiresTwoFa?: boolean;
  twofaPending?: boolean;
  twofaSetupRequired?: boolean;
  deviceToken?: string;
}

export interface TwoFaSetupResponse {
  secret: string;
  qrCodeUrl: string;
  otpauthUrl: string;
}

export interface TwoFaStatusResponse {
  twofaEnabled: boolean;
  enforcement: TwoFaEnforcement;
  mode: TwoFaMode;
  trustedDeviceDays: number;
}

export interface TrustedDeviceInfo {
  id: string;
  deviceName: string;
  ipAddress: string | null;
  expiresAt: string;
  createdAt: string;
}

export interface ApiError {
  error: string;
}

// Screen device types
export interface Device {
  id: string;
  name: string;
  token: string;
  roomId: string;
  deviceType: string;
  isActive: boolean;
  lastSeenAt: string | null;
  firmwareVersion: string | null;
  pendingFirmwareVersion: string | null;  // Version scheduled for next update
  hasUpdate?: boolean;
  latestVersion?: string | null;
  room?: MeetingRoom;
  createdAt?: string;
  updatedAt?: string;
}

// Firmware types for OTA updates
export interface Firmware {
  id: string;
  version: string;
  deviceType: string;
  filename: string;
  size: number;
  checksum: string;
  releaseNotes: string;
  isActive: boolean;
  createdAt: string;
}

export interface GuestVisit {
  id: string;
  bookingId: string;
  guestName: string;
  guestEmail: string | null;
  guestCompany: string | null;
  expectedArrival: string;
  checkedInAt: string | null;
  checkedOutAt: string | null;
  bookingTitle?: string;
  bookingEndTime?: string;
  roomName?: string;
  roomClosingHour?: number | null;
  organizerName?: string;
  organizerCompany?: string;
}

// LDAP types
export interface LdapRoleMapping {
  ldapGroupDn: string;
  appRole: string;
}

export interface LdapConfig {
  id: string;
  companyId: string;
  isEnabled: boolean;
  serverUrl: string;
  bindDn: string;
  searchBase: string;
  userFilter: string;
  usernameAttribute: string;
  emailAttribute: string;
  nameAttribute: string;
  groupSearchBase: string | null;
  groupFilter: string | null;
  groupMemberAttribute: string;
  roleMappings: LdapRoleMapping[];
  defaultRole: string;
  syncIntervalHours: number;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncMessage: string | null;
  lastSyncUserCount: number | null;
  useStarttls: boolean;
  tlsRejectUnauthorized: boolean;
  connectionTimeoutMs: number;
  createdAt: string;
  updatedAt: string;
}

export interface LdapSyncResult {
  created: number;
  updated: number;
  disabled: number;
  reactivated: number;
  errors: string[];
  totalLdapUsers: number;
}

// SSO types
export interface SsoConfig {
  id: string;
  companyId: string;
  isEnabled: boolean;
  protocol: SsoProtocol;
  displayName: string;
  oidcIssuerUrl: string | null;
  oidcClientId: string | null;
  oidcScopes: string | null;
  samlEntryPoint: string | null;
  samlIssuer: string | null;
  samlCert: string | null;
  samlCallbackUrl: string | null;
  autoCreateUsers: boolean;
  defaultRole: string;
  emailDomains: string[];
  createdAt: string;
  updatedAt: string;
}

export interface SsoDiscoveryResult {
  hasSso: boolean;
  configId?: string;
  protocol?: SsoProtocol;
  displayName?: string;
}

// Calendar feed token types
export type CalendarTokenScope = 'my_bookings' | 'room' | 'park_rooms';

export interface CalendarToken {
  id: string;
  scope: CalendarTokenScope;
  roomId: string | null;
  label: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}

export interface CalendarTokenCreated extends CalendarToken {
  rawToken: string; // Only present immediately after creation
  feedUrl: string;  // Complete subscription URL
}

// Hot desk types
export type DeskQuotaType = 'per_user' | 'per_company';

export interface Desk {
  id: string;
  parkId: string;
  name: string;
  description: string | null;
  floor: string | null;
  isActive: boolean;
  features?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface DeskBooking {
  id: string;
  deskId: string;
  userId: string;
  bookingDate: string; // 'YYYY-MM-DD'
  status: 'confirmed' | 'cancelled';
  desk?: Desk;
  createdAt?: string;
  updatedAt?: string;
}

export interface DeskQuotaStatus {
  quotaType: DeskQuotaType | null;
  monthlyQuota: number | null;
  usedThisMonth: number;
  remainingThisMonth: number | null; // null = unlimited
  blockedWeekdays: number[]; // 0=Sun … 6=Sat
  weekStartDay: number;      // 0=Sun, 1=Mon …
}

export interface UserDeskQuota {
  id: string;
  parkId: string;
  userId: string;
  monthlyQuota: number;
  user?: { id: string; name: string; email: string };
  createdAt?: string;
  updatedAt?: string;
}

export interface ParkDeskQuota {
  deskQuotaType: DeskQuotaType | null;
  monthlyDeskQuota: number | null;
  blockedWeekdays: number[]; // 0=Sun … 6=Sat
  weekStartDay: number;      // 0=Sun, 1=Mon …
  overrides: UserDeskQuota[];
}
